import { ChangeDetectionStrategy, Component, computed, inject, signal } from "@angular/core";
import {
  ConfirmPopupComponent,
  ConfirmPopupData,
  EmptyStateComponent,
  IButtonDirective,
  IDataTableComponent,
  IDataTablePrefixDirective,
  IDataTableSuffix,
  IDropdownMenuComponent,
  IDropdownMenuItemDirective,
  IDropdownMenuSeparatorComponent,
  IDropdownMenuTriggerDirective,
  IIconComponent,
  ITableDataSource,
  ITableDirective,
  ITableHeadDirective,
  ITablePagination,
  ITableRowDirective,
  ITableTextFilterConfig,
  ITableThDirective,
  ModalService,
  SortByConfig,
  SortingOption,
  SpinnerComponent,
  ToasterService,
} from "@front/m19-ui";
import {
  AccountState,
  BidderRun,
  Brand,
  CampaignType,
  Currency,
  StrategyAsin,
  StrategyDspFunnel,
} from "@front/m19-api-client";
import { CampaignTypeWithTacos, Marketplaces, SegmentEx, StrategyEx } from "@front/m19-models";
import { combineLatest, map, of, startWith, switchMap } from "rxjs";
import {
  AccountMarketplaceService,
  AccountSelectionService,
  AuthService,
  SbStrategyService,
  SegmentService,
} from "@front/m19-services";
import { CAMPAIGN_TYPE } from "@front/m19-injection-token";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { toObservable, toSignal } from "@angular/core/rxjs-interop";
import {
  CampaignTypeStrategyLine,
  StrategyGridService,
  StrategyLine,
} from "@m19-board/strategies/strategy-grid/strategy-grid.service";
import { DatePipe } from "@angular/common";
import { DateTime } from "luxon";
import { StrategyGridLineComponent } from "@m19-board/strategies/strategy-grid/strategy-grid-line.component";
import { Router, RouterLink } from "@angular/router";
import {
  CompactFilterComponent,
  Filter,
  FilterCategory,
} from "@m19-board/shared/compact-filter/compact-filter.component";
import { CompactFilterService } from "@m19-board/shared/compact-filter/compact-filter.service";
import {
  passesStrategyGridFilters,
  StrategyGridFilterCategory,
} from "@m19-board/strategies/strategy-grid/strategy-grid-filters";

export type StrategyGridColumn =
  | "asins"
  | "info"
  | "tactics"
  | "brand"
  | "adline"
  | "creativeType"
  | "targetings"
  | "funnel"
  | "strategies";
const strategyGridColumnsTitles: Record<StrategyGridColumn, string> = {
  adline: "sb-form-asins.ad_line",
  asins: "common.asins",
  brand: "common.brand",
  funnel: "strategy-page.funnel",
  info: "strategy-grid.informations",
  strategies: "common.strategies",
  tactics: "strategy-table.tactics_strategies",
  targetings: "common.targetings",
  creativeType: "strategy-page.creative_type",
};

@Component({
  selector: "app-strategy-grid",
  imports: [
    IDataTableComponent,
    TranslocoDirective,
    ITableDirective,
    ITableHeadDirective,
    ITableRowDirective,
    ITableThDirective,
    IButtonDirective,
    IDataTablePrefixDirective,
    IDataTableSuffix,
    CompactFilterComponent,
    DatePipe,
    IDropdownMenuComponent,
    IDropdownMenuItemDirective,
    IDropdownMenuSeparatorComponent,
    IDropdownMenuTriggerDirective,
    StrategyGridLineComponent,
    SpinnerComponent,
    EmptyStateComponent,
    RouterLink,
    IIconComponent,
  ],
  providers: [StrategyGridService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: "./strategy-grid.component.html",
})
export class StrategyGridComponent<T extends CampaignTypeWithTacos> {
  private readonly sbStrategyService = inject(SbStrategyService);
  private readonly accountSelectionService = inject(AccountSelectionService);
  private readonly accountMarketplaceService = inject(AccountMarketplaceService);
  private readonly authService = inject(AuthService);
  private readonly modalService = inject(ModalService);
  private readonly segmentService = inject(SegmentService);
  private readonly translocoService = inject(TranslocoService);
  private readonly strategyListService = inject(StrategyGridService);
  private readonly toasterService = inject(ToasterService);
  private readonly strategyGridService = inject(StrategyGridService);
  protected readonly campaignType = inject<T>(CAMPAIGN_TYPE);
  private readonly router = inject(Router);
  private readonly compactFilterService = inject(CompactFilterService);

  private readonly DEFAULT_PAGE_SIZE = 15;
  protected readonly strategyGridColumnsTitles = strategyGridColumnsTitles;
  protected readonly CampaignType = CampaignType;

  gridColumns: StrategyGridColumn[] = this.getColumnsForCampaignType(this.campaignType);

  selectedStrategies = signal<Set<StrategyLine<StrategyEx>>>(new Set(), { equal: () => false });
  deleteStrategiesLoading = signal(false);

  deleteStrategiesLabel = computed(() => {
    const count = this.selectedStrategies().size;
    return this.translocoService.translate("common.delete_x_strategies", { count });
  });

  locale = toSignal(this.authService.userLocale$, { requireSync: true });
  currency = toSignal<Currency>(
    this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(
      map((am) => Marketplaces[am.marketplace].currency),
    ),
    { requireSync: true },
  );
  accountMarketplace = toSignal(this.accountSelectionService.singleAccountMarketplaceSelection$, { requireSync: true });
  organizationId = toSignal<number>(
    this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(map((am) => am.resourceOrganizationId!)),
    { requireSync: true },
  );
  isReadonly = toSignal(this.accountSelectionService.readOnlyMode$, { requireSync: true });

  readonly lastBidderRun = toSignal(
    this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(
      switchMap((am) =>
        this.accountMarketplaceService.listBidderRuns(am.accountId, am.marketplace, am.dspAdvertiserId).pipe(
          map((runs: BidderRun[] | undefined) => {
            if (!runs) {
              return;
            }
            const bidderRun = runs.find((run) => run.campaignType == this.campaignType);
            if (bidderRun) {
              return {
                ...bidderRun,
                lastBidderStartLocal: DateTime.fromISO(bidderRun.lastBidderStart!, { zone: "utc" }).toLocal().toISO(),
              };
            }
            return undefined;
          }),
        ),
      ),
    ),
  );

  segmentIndex = toSignal<Map<number, SegmentEx>>(
    this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(
      switchMap((am) => this.segmentService.getSegments(am.accountId, am.marketplace)),
      startWith(new Map<number, SegmentEx>()),
    ),
    { requireSync: true },
  );
  brands = toSignal<Brand[]>(
    this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(
      switchMap((am) => this.sbStrategyService.getBrands(am.accountId, am.marketplace)),
      startWith([]),
    ),
    { requireSync: true },
  );

  protected paginationConfig = signal<ITablePagination>(new ITablePagination(this.DEFAULT_PAGE_SIZE));
  protected textFilterConfig = new ITableTextFilterConfig<StrategyLine<StrategyEx>>((strategy, filter) => {
    return this.doesExternalFilterPass(strategy, filter);
  });
  protected readonly sortByOptions: SortingOption<CampaignTypeStrategyLine[T]>[] = [
    {
      label: this.translocoService.translate("common.activated"),
      sortByField: "state",
    },
    {
      label: this.translocoService.translate("common.paused"),
      sortByField: "state",
      direction: "asc",
    },
  ];
  protected readonly sortByConfig = new SortByConfig(this.sortByOptions, this.sortByOptions[0]);
  protected readonly filters: Filter<StrategyGridFilterCategory>[] = this.getFiltersForCampaignType(this.campaignType);
  private readonly selectedFilters = signal<Filter<StrategyGridFilterCategory>[]>([]);

  protected readonly rawDatasource$ = this.strategyGridService.datasource$;
  protected readonly rawDatasource = toSignal(this.rawDatasource$);
  private readonly filteredDatasource$ = combineLatest([this.rawDatasource$, toObservable(this.selectedFilters)]).pipe(
    map(([lines, filters]) => lines.filter((line) => passesStrategyGridFilters(line.strategy, filters))),
  );
  protected readonly dataSource = new ITableDataSource<CampaignTypeStrategyLine[T]>(
    this.filteredDatasource$,
    this.sortByConfig,
    this.paginationConfig(),
    this.textFilterConfig,
  );

  doesExternalFilterPass(strat: StrategyLine<StrategyEx>, filter: string): boolean {
    if (!filter) return true;

    const nameMatches = strat.strategyName.toLowerCase().includes(filter.toLowerCase());

    return nameMatches || strat.asins.some((asin: StrategyAsin) => filter == asin.asin);
  }

  onFilterChange(filters: Filter<StrategyGridFilterCategory>[]): void {
    this.selectedFilters.set(filters);
    this.paginationConfig().setPagination({ ...this.paginationConfig().pagination(), pageIndex: 0 });
  }

  deleteSelectedStrategies(): void {
    this.deleteStrategiesLoading.set(true);

    const modalRef = this.modalService.openModal<ConfirmPopupData, boolean>(ConfirmPopupComponent, {
      modalTitle: this.translocoService.translate("activity-service.strategy_deletion"),
      data: {
        message: this.translocoService.translate("strategy-grid.are_you_sure_you_want_to_delete_strategies", [
          this.selectedStrategies().size,
        ]),
        confirmLabel: this.translocoService.translate("common.delete"),
      },
    });

    modalRef
      .afterClosed()
      .pipe(
        switchMap((result) =>
          result
            ? this.strategyListService.deleteStrategies(
                Array.from(this.selectedStrategies()),
                this.accountMarketplace()!.resourceOrganizationId!,
              )
            : of(undefined),
        ),
      )
      .subscribe({
        next: (result) => {
          this.selectedStrategies.set(new Set());
          this.deleteStrategiesLoading.set(false);
          if (result) {
            this.toasterService.success(this.translocoService.translate("strategy-grid.strategies_deleted"));
          }
        },
        error: (err) => {
          this.deleteStrategiesLoading.set(false);
          this.toasterService.error(this.translocoService.translate(err));
        },
      });

    modalRef.afterCancelled().subscribe(() => {
      this.deleteStrategiesLoading.set(false);
    });
  }

  bulkImportStrategies(): void {
    if (this.campaignType === CampaignType.SP) {
      this.router.navigate(["/bulk/sponsored-product"]);
      return;
    }
    if (this.campaignType === CampaignType.SD) {
      this.router.navigate(["/bulk/sponsored-display"]);
      return;
    }
    if (this.campaignType === CampaignTypeWithTacos.TACOS) {
      this.router.navigate(["/bulk/tacos"]);
    }
  }

  onExportStrategies(): void {
    if (this.campaignType === CampaignTypeWithTacos.TACOS) {
      this.strategyListService.exportTacosStrategies(this.accountMarketplace()!);
    } else {
      this.strategyListService.exportStrategies(this.campaignType, this.accountMarketplace()!);
    }
  }

  pushBidderRun(): void {
    if (this.isReadonly()) {
      return;
    }
    const bidderRun = this.lastBidderRun();
    const isBidderRunInProgress = !bidderRun || bidderRun.bidderRunExpected;
    if (isBidderRunInProgress) {
      this.toasterService.info(this.translocoService.translate("strategy-list.push_bidder_run_not_available"));
      return;
    }
    if (this.accountMarketplace()?.state !== AccountState.BIDDER_ON) {
      this.toasterService.error(
        this.translocoService.translate("account-setting.bidder_not_on", [
          this.accountMarketplace()?.accountId,
          this.accountMarketplace()?.marketplace,
        ]),
      );
      return;
    }
    this.accountMarketplaceService
      .runBidder(
        this.accountMarketplace().accountId!,
        this.accountMarketplace().marketplace!,
        this.accountMarketplace().dspAdvertiserId!,
        this.lastBidderRun()!.campaignType,
      )
      .subscribe({
        next: () => {
          this.toasterService.success(
            this.translocoService.translate("account-setting.push_requested", {
              campaignType: this.campaignType,
            }),
          );
        },
        error: (_) => {
          this.toasterService.error(
            this.translocoService.translate("account-setting.campaign_structure_push_error", {
              campaignType: this.campaignType,
            }),
          );
        },
      });
  }

  selectStrategy(strategyLine: StrategyLine<StrategyEx>): void {
    this.selectedStrategies.update((selected) => {
      if (selected.has(strategyLine)) {
        selected.delete(strategyLine);
      } else {
        selected.add(strategyLine);
      }
      return selected;
    });
  }

  private getFiltersForCampaignType(campaignType: T): Filter<StrategyGridFilterCategory>[] {
    if (campaignType !== CampaignType.DSP) {
      return [];
    }
    return [this.compactFilterService.getFilterWithOptions(FilterCategory.DspFunnel, Object.values(StrategyDspFunnel))];
  }

  private getColumnsForCampaignType(campaignType: T): StrategyGridColumn[] {
    if (campaignType === CampaignTypeWithTacos.TACOS) {
      return ["strategies"];
    }
    return ["asins", "info", ...this.getCampaignTypeSpecificColumns(campaignType)];
  }

  private getCampaignTypeSpecificColumns(campaignType: CampaignType): StrategyGridColumn[] {
    switch (campaignType) {
      case CampaignType.SP:
        return ["tactics"];
      case CampaignType.SB:
        return ["brand", "adline"];
      case CampaignType.SD:
        return ["targetings"];
      case CampaignType.DSP:
        return ["funnel", "creativeType"];
      default:
        return [];
    }
  }
}
